import { lighten } from '@mui/material'
import { alpha, Theme, useTheme } from '@mui/material/styles'
import { useMemo } from 'react'
import { GroupBase, StylesConfig } from 'react-select'

export type MuiSelectSize = 'small' | 'medium' | 'large'

// Ajustado exactamente a la escala de alturas por defecto de Material UI (OutlinedInput)
const sizeConfig = {
  small: {
    minHeight: '40px',
    fontSize: '0.875rem', // 14px
    paddingY: '2px',
  },
  medium: {
    minHeight: '56px', // Estándar nativo MUI
    fontSize: '1rem', // 16px
    paddingY: '8px',
  },
  large: {
    minHeight: '64px',
    fontSize: '1.125rem', // 18px
    paddingY: '12px',
  },
}

export const getSelectStyles = <
  OptionType = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  theme: Theme,
  error: boolean = false,
  size: MuiSelectSize = 'small',
): StylesConfig<OptionType, IsMulti, Group> => {
  const isDark = theme.palette.mode === 'dark'
  const currentSize = sizeConfig[size]

  return {
    // --- CONTROL (Contenedor principal con estilo OutlinedInput) ---
    control: (provided, state) => {
      let borderColor = isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
      let focusBorderColor = theme.palette.primary.main
      let backgroundColor = theme.palette.background.paper

      if (error) {
        borderColor = theme.palette.error.main
        focusBorderColor = theme.palette.error.main
      } else if (state.isFocused) {
        borderColor = theme.palette.primary.main
      } else if (state.isDisabled) {
        borderColor = alpha(theme.palette.text.disabled, 0.26)
        backgroundColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
      }

      return {
        ...provided,
        backgroundColor,
        borderColor,
        borderRadius: theme.shape.borderRadius, // Garantiza esquinas identicas a TextField
        color: theme.palette.text.primary,
        minHeight: currentSize.minHeight,
        boxSizing: 'border-box',
        fontSize: currentSize.fontSize,
        transition: theme.transitions.create(['border-color', 'box-shadow'], {
          duration: theme.transitions.duration.shorter,
        }),
        boxShadow: state.isFocused ? `0 0 0 1px ${focusBorderColor}` : 'none',
        '&:hover': {
          borderColor: error
            ? theme.palette.error.main
            : state.isFocused
              ? focusBorderColor
              : theme.palette.text.primary,
        },
      }
    },

    // --- VALUE CONTAINER ---
    valueContainer: (provided) => ({
      ...provided,
      height: '100%',
      paddingTop: currentSize.paddingY,
      paddingBottom: currentSize.paddingY,
      paddingLeft: theme.spacing(1.75), // 14px (MUI OutlinedInput padding-left)
      paddingRight: theme.spacing(1),
      alignItems: 'center',
    }),

    // --- INPUT (Cursor) ---
    input: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
      margin: 0,
      padding: 0,
    }),

    // --- SINGLE VALUE ---
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
      marginLeft: 0,
    }),

    // --- PLACEHOLDER ---
    placeholder: (provided) => ({
      ...provided,
      color: theme.palette.text.secondary,
      opacity: 0.7,
      marginLeft: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),

    // --- MENU DROPDOWN ---
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[8], // Sombra flotante tipo Popover de MUI
      zIndex: theme.zIndex.modal + 1,
      overflow: 'hidden',
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: theme.zIndex.modal + 1,
    }),

    // --- OPCIONES DEL MENÚ ---
    option: (provided, state) => {
      let backgroundColor = 'transparent'
      let color = theme.palette.text.primary

      if (state.isSelected) {
        backgroundColor = alpha(theme.palette.primary.main, 0.12)
        color = theme.palette.primary.main
      } else if (state.isFocused) {
        backgroundColor = theme.palette.action.hover
      }

      return {
        ...provided,
        backgroundColor,
        color,
        cursor: 'pointer',
        fontSize: currentSize.fontSize,
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`, // 8px 16px (MUI MenuItem spacing)
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shortest,
        }),
        '&:active': {
          backgroundColor: alpha(theme.palette.primary.main, 0.24),
        },
      }
    },

    // --- MULTI VALUE (Chips) ---
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.isDisabled
        ? alpha(theme.palette.action.disabled, 0.12)
        : alpha(theme.palette.primary.main, 0.12),
      borderRadius: (theme.shape.borderRadius as number) / 2,
      margin: '2px',
    }),

    multiValueLabel: (provided) => ({
      ...provided,
      color: isDark ? lighten(theme.palette.primary.main, 0.4) : theme.palette.primary.dark,
      fontWeight: 500,
      fontSize: size === 'small' ? '0.75rem' : '0.85rem',
      padding: '2px 6px',
    }),

    multiValueRemove: (provided) => ({
      ...provided,
      color: theme.palette.primary.main,
      cursor: 'pointer',
      borderRadius: `0 ${(theme.shape.borderRadius as number) / 2}px ${(theme.shape.borderRadius as number) / 2}px 0`,
      ':hover': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      },
    }),

    // --- INDICADORES (Flecha e ícono de limpiar) ---
    indicatorsContainer: (provided) => ({
      ...provided,
    }),

    dropdownIndicator: (provided) => ({
      ...provided,
      padding: size === 'small' ? '6px' : '8px',
      color: error ? theme.palette.error.main : theme.palette.action.active,
      '&:hover': {
        color: error ? theme.palette.error.dark : theme.palette.text.primary,
      },
    }),

    clearIndicator: (provided) => ({
      ...provided,
      padding: size === 'small' ? '6px' : '8px',
      color: theme.palette.action.active,
      '&:hover': {
        color: theme.palette.error.main,
      },
    }),
    // Ocultar la línea divisoria vertical para que parezca un input 100% nativo de MUI
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
  }
}

export const useMuiSelectStyles = <
  OptionType = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  error: boolean = false,
  size: MuiSelectSize = 'small',
): StylesConfig<OptionType, IsMulti, Group> => {
  const theme = useTheme()

  return useMemo(() => getSelectStyles<OptionType, IsMulti, Group>(theme, error, size), [theme, error, size])
}
